import {
  Component,
  HostBinding,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BsModalService } from 'ngx-bootstrap/modal';
import { LoadingDialogComponent } from 'src/app/components';
import {
  BookingService,
  ExamService,
  LayoutService,
  NotificationService,
} from 'src/app/services';
import { WizardStep } from 'src/app/types';
import { BreadcrumbService } from 'xng-breadcrumb';
import { Step1ProfileCheckFormComponent } from '../step1-profile-check-form';
import { Step2EventLocationFormComponent } from '../step2-event-location-form';
import { Step3TestTimeFormComponent } from '../step3-test-time-form';
import { Step4AdditionalQuestionsFormComponent } from '../step4-additional-questions-form';

@Component({
  selector: 'app-exam-booking-form-default',
  templateUrl: './exam-booking-form-default.component.html',
  encapsulation: ViewEncapsulation.None,
})
export class ExamBookingFormDefaultComponent
  implements OnInit
{
  @HostBinding('class.form-page-component') hostClass =
    true;

  @ViewChild('step1', { static: false })
  step1Component!: Step1ProfileCheckFormComponent;
  @ViewChild('step2', { static: false })
  step2Component!: Step2EventLocationFormComponent;
  @ViewChild('step3', { static: false })
  step3Component!: Step3TestTimeFormComponent;
  @ViewChild('step4', { static: false })
  step4Component!: Step4AdditionalQuestionsFormComponent;

  isPayBtnClicked = false;
  stepId = '';
  currentStepIndex = 0;

  steps: WizardStep[] = [
    {
      id: 'step-1',
      label: 'label.profile-check',
      isValid: false,
      isRelevant: true,
    },
    {
      id: 'step-2',
      label: 'label.test-pre-check',
      isValid: false,
      isRelevant: true,
    },
    {
      id: 'step-3',
      label: 'label.test-event-select',
      isValid: false,
      isRelevant: true,
    },
    {
      id: 'step-4',
      label: 'title.additional-questions',
      isValid: false,
      isRelevant: true,
    },
    {
      id: 'step-5',
      label: 'title.summary',
      isValid: false,
      isRelevant: true,
    },
  ];

  stepLoading = false;

  constructor(
    private activatedRoute: ActivatedRoute,
    private bookingService: BookingService,
    private breadcrumbService: BreadcrumbService,
    private examService: ExamService,
    private layoutService: LayoutService,
    private modalService: BsModalService,
    private note: NotificationService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.breadcrumbService.set(
      '@examName',
      this.examTestName
    );

    // Show or hide steps based on the selected test template
    switch (
      this.bookingService.formState.item.test.templateName
    ) {
      case 'PhaST':
      case 'ITB-Business':
      case 'ITB-Science':
      case 'ITB-Technology':
      case 'TM-WISO':
      case 'GSAT': {
        this.steps[1].isRelevant = false;
        break;
      }
      // Not handling the SdV case here, since that will never happen in this form.
    }
  }

  get relevantSteps(): WizardStep[] {
    return this.steps.filter((step) => step.isRelevant);
  }

  async handleNext(): Promise<void> {
    this.stepLoading = true;

    let isValid = false;

    try {
      switch (this.currentStepIndex) {
        case 0:
          isValid = await this.step1Component.submit();
          this.setProfileCheckFormValid(isValid);
          break;
        case 1:
          isValid = await this.step2Component.submit();
          this.setEventLocationFormValid(isValid);
          break;
        case 2:
          isValid = await this.step3Component.submit();
          this.setTimeSlotFormValid(isValid);
          break;
        case 3:
          isValid = await this.step4Component.submit();
          this.setAdditionalQuestionsFormValid(isValid);
          break;
      }
    } catch (e) {
      this.stepLoading = false;
      throw e;
    }

    if (isValid) {
      this.nextStep();
    }
  }

  get isLastStep(): boolean {
    return this.currentStepIndex === this.steps.length - 1;
  }

  get isFirstStep(): boolean {
    return this.currentStepIndex === 0;
  }

  get isHandset$() {
    return this.layoutService.isHandset$;
  }

  private nextStep(): void {
    if (
      this.steps[this.currentStepIndex].isValid &&
      !this.isLastStep
    ) {
      do {
        this.currentStepIndex++;
      } while (
        !this.steps[this.currentStepIndex].isRelevant &&
        !this.isLastStep
      );
    }
  }

  previousStep(): void {
    this.stepLoading = true;
    if (!this.isFirstStep) {
      do {
        this.currentStepIndex--;
      } while (
        !this.steps[this.currentStepIndex].isRelevant &&
        !this.isFirstStep
      );
    }
  }

  loadingStepComplete(): void {
    // Set timeout to avoid ExpressionChangedAfterItHasBeenCheckedError
    setTimeout(() => {
      this.stepLoading = false;
    });
  }

  setProfileCheckFormValid(value: boolean): void {
    this.steps[0].isValid = value;
  }

  setEventLocationFormValid(value: boolean): void {
    this.steps[1].isValid = value;
  }

  setTimeSlotFormValid(value: boolean): void {
    this.steps[2].isValid = value;
  }

  setAdditionalQuestionsFormValid(value: boolean): void {
    this.steps[3].isValid = value;
  }

  setOverviewFormValid(value: boolean) {
    this.steps[4].isValid = value;
  }

  get overviewFormValid(): boolean {
    return this.steps[4].isValid;
  }

  doPayment(): void {
    this.isPayBtnClicked = true;

    const modalRef = this.modalService.show(
      LoadingDialogComponent,
      {
        class: 'modal-sm',
        ignoreBackdropClick: true,
      }
    );

    this.bookingService
      .doPayment(this.bookingId)
      .finally(() => {
        modalRef.hide();
        this.isPayBtnClicked = false;
      });
  }

  deleteBooking(): void {
    this.bookingService
      .deleteBooking(this.bookingId)
      .then(() => {
        this.bookingService.formState = {
          loading: false,
          item: BookingService.factoryItem(),
        };
      })
      .then(() => this.router.navigate(['/home']))
      .catch((e) => {
        if (e.code === 409) {
          this.note.error(
            'error.notification-payment-started'
          );
        } else {
          this.note.error('error.general');
        }
        return Promise.reject(e);
      });
  }

  get bookingId(): number {
    return this.bookingService.formState.item.bookingId;
  }

  get examIdParam(): number {
    return Number(
      this.activatedRoute.snapshot.paramMap
        .get('id')
        ?.match(/^\d+/i)
    );
  }

  get examTestName(): string {
    const examName =
      this.examService.listState.response.items.find(
        (exam) => exam.id === this.examIdParam
      )?.testname;
    return examName ?? '';
  }
}
